import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useShopFile } from '../../components/ShopFileLayout';
import {
  shopifyScopesBadgeKey,
  shopifyScopesStatus,
} from '../../lib/shop-file';
import { PageBody } from '../../components/Page';
import { Badge } from '../../components/Pill';
import { StatusBanner } from '../../components/StatusBanner';
import { intlLocale } from '../../i18n/formats';
import { useLocale } from '../../i18n/LocaleProvider';
import type { SupportedLocale } from '../../i18n';

/** A date-time as the reader's locale writes it, or a dash. */
function when(
  value: string | null | undefined,
  locale: SupportedLocale,
): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(intlLocale(locale), {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/** One labelled fact in a definition grid. */
function Fact({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-900 break-words">{children}</dd>
    </div>
  );
}

/** A card with a heading, and the reason it could not be read when it could not. */
function Section({
  title,
  unavailable,
  children,
}: {
  readonly title: string;
  readonly unavailable?: string;
  readonly children: ReactNode;
}) {
  const { t } = useLocale();
  return (
    <section className="card space-y-3">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      {unavailable ? (
        <StatusBanner tone="warning">
          {t('shopFile.sectionUnavailable', { reason: unavailable })}
        </StatusBanner>
      ) : (
        children
      )}
    </section>
  );
}

/** Yes/no as a badge, so a column of booleans reads at a glance. */
function YesNo({ value }: { readonly value: boolean }) {
  const { t } = useLocale();
  return (
    <Badge tone={value ? 'green' : 'gray'}>
      {value ? t('common.yes') : t('common.no')}
    </Badge>
  );
}

/**
 * The shop file's first tab: who the merchant is, whether the app can work for them, and what is
 * open right now.
 *
 * Open Needs-Attention items are LISTED, never actionable here: every type needs a fact or a
 * decision only the merchant holds (work order AC-G1a), so the most an agent can do is tell the
 * merchant which item to open.
 */
export function OverviewTab() {
  const { t, locale } = useLocale();
  const { file } = useShopFile();
  const {
    identity,
    setup,
    legal,
    subscription,
    live,
    shopifyScopes,
    unavailable,
  } = file;
  const scopesStatus = shopifyScopes
    ? shopifyScopesStatus(shopifyScopes)
    : null;
  const avvOutdated =
    legal.avvVersion !== null && legal.avvVersion !== legal.currentAvvVersion;
  const agbOutdated =
    legal.agbVersion !== null && legal.agbVersion !== legal.currentAgbVersion;

  return (
    <PageBody>
      <div className="grid gap-5 lg:grid-cols-2">
        <Section title={t('shopFile.overview.identity')}>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Fact label={t('shopFile.overview.domain')}>
              <span className="font-mono">{identity.shopDomain}</span>
            </Fact>
            <Fact label={t('shopFile.overview.legalName')}>
              {identity.legalName ?? '—'}
            </Fact>
            <Fact label={t('shopFile.overview.country')}>
              {identity.country ?? '—'}
            </Fact>
            <Fact label={t('shopFile.overview.locale')}>
              {identity.locale} · {identity.timezone}
            </Fact>
            <Fact label={t('shopFile.overview.supportEmail')}>
              {identity.supportEmail ?? '—'}
            </Fact>
            <Fact label={t('shopFile.overview.installedAt')}>
              {when(identity.installedAt, locale)}
            </Fact>
            {identity.pendingDeletion && (
              <Fact label={t('shopFile.overview.deletionScheduledAt')}>
                {when(identity.deletionScheduledAt, locale)}
              </Fact>
            )}
          </dl>
        </Section>

        <Section
          title={t('shopFile.overview.subscription')}
          unavailable={unavailable['subscription']}
        >
          {subscription ? (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Fact label={t('shopFile.overview.tier')}>
                {subscription.tier}
              </Fact>
              <Fact label={t('shopFile.overview.status')}>
                {subscription.status}
              </Fact>
              <Fact label={t('shopFile.overview.trialEnd')}>
                {subscription.isInTrial
                  ? when(subscription.trialEnd, locale)
                  : t('shopFile.overview.notInTrial')}
              </Fact>
              <Fact label={t('shopFile.overview.periodEnd')}>
                {when(subscription.currentPeriodEnd, locale)}
              </Fact>
              {subscription.pendingTier && (
                <Fact label={t('shopFile.overview.pendingTier')}>
                  {subscription.pendingTier}
                </Fact>
              )}
            </dl>
          ) : (
            <p className="text-sm text-gray-600">
              {t('shopFile.noSubscription')}
            </p>
          )}
        </Section>

        <Section title={t('shopFile.overview.setup')}>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Fact label={t('shopFile.overview.numbering')}>
              <YesNo value={setup.numberingConfigured} />
            </Fact>
            <Fact label={t('shopFile.overview.invoiceCounter')}>
              {setup.currentInvoiceNo ?? '—'}
              {setup.invoicePrefix ? ` · ${setup.invoicePrefix}` : ''}
            </Fact>
            <Fact label={t('shopFile.overview.taxScheme')}>
              {setup.taxScheme ?? '—'}
            </Fact>
            <Fact label={t('shopFile.overview.vatId')}>
              <YesNo value={setup.hasVatId} />
            </Fact>
            <Fact label={t('shopFile.overview.autoSend')}>
              <YesNo value={!setup.disableAutoSend} />
            </Fact>
            <Fact label={t('shopFile.overview.eInvoice')}>
              <YesNo value={setup.enableEInvoice} />
            </Fact>
            <Fact label={t('shopFile.overview.vatAccountingMethod')}>
              {setup.vatAccountingMethod}
            </Fact>
          </dl>
        </Section>

        <Section
          title={t('shopFile.overview.shopifyScopes')}
          unavailable={unavailable['shopifyScopes']}
        >
          {shopifyScopes && scopesStatus && (
            <div className="space-y-2">
              <Badge
                tone={
                  scopesStatus === 'approved'
                    ? 'green'
                    : scopesStatus === 'missing'
                      ? 'amber'
                      : 'gray'
                }
              >
                {t(shopifyScopesBadgeKey(scopesStatus))}
              </Badge>
              {scopesStatus === 'missing' && (
                <p className="text-sm text-gray-700">
                  {t('shopFile.overview.scopesMissingDetail', {
                    scopes: shopifyScopes.missingScopes.join(', '),
                  })}
                </p>
              )}
              {scopesStatus === 'unknown' && (
                <p className="text-sm text-gray-700">
                  {t('shopFile.overview.scopesUnknownDetail')}
                </p>
              )}
              <p className="text-xs text-gray-500">
                {t('shopFile.overview.scopesNoDate')}
              </p>
            </div>
          )}
        </Section>

        <Section title={t('shopFile.overview.email')}>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Fact label={t('shopFile.overview.mailPath')}>
              {setup.usesFallbackEmail
                ? t('shopFile.overview.mailPathFallback')
                : setup.smtpConfigured
                  ? t('shopFile.overview.mailPathOwn')
                  : t('shopFile.overview.mailPathNone')}
            </Fact>
            <Fact label={t('shopFile.overview.smtpFrom')}>
              {setup.smtpFromAddress ?? '—'}
            </Fact>
            <Fact label={t('shopFile.overview.smtpFailures')}>
              <Badge tone={setup.smtpConsecutiveFailures > 0 ? 'red' : 'green'}>
                {setup.smtpConsecutiveFailures}
              </Badge>
            </Fact>
            <Fact label={t('shopFile.overview.lastDelivery')}>
              {when(setup.smtpLastDeliveryAt, locale)}
            </Fact>
            {setup.smtpLastTestError && (
              <Fact label={t('shopFile.overview.lastTestError')}>
                <span className="text-red-700">{setup.smtpLastTestError}</span>
              </Fact>
            )}
          </dl>
        </Section>

        <Section title={t('shopFile.overview.legal')}>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Fact label={t('shopFile.overview.avv')}>
              {legal.avvAcceptedAt ? (
                <>
                  {legal.avvVersion} · {when(legal.avvAcceptedAt, locale)}{' '}
                  {avvOutdated && (
                    <Badge tone="amber">
                      {t('shopFile.overview.outdated', {
                        version: legal.currentAvvVersion,
                      })}
                    </Badge>
                  )}
                </>
              ) : (
                <Badge tone="red">{t('shopFile.overview.notAccepted')}</Badge>
              )}
            </Fact>
            <Fact label={t('shopFile.overview.agb')}>
              {legal.agbAcceptedAt ? (
                <>
                  {legal.agbVersion} · {when(legal.agbAcceptedAt, locale)}{' '}
                  {agbOutdated && (
                    <Badge tone="amber">
                      {t('shopFile.overview.outdated', {
                        version: legal.currentAgbVersion,
                      })}
                    </Badge>
                  )}
                </>
              ) : (
                <Badge tone="red">{t('shopFile.overview.notAccepted')}</Badge>
              )}
            </Fact>
          </dl>
        </Section>

        <Section
          title={t('shopFile.overview.live')}
          unavailable={unavailable['live']}
        >
          {live && (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Fact label={t('shopFile.overview.lastInvoice')}>
                {when(live.lastInvoiceIssuedAt, locale)}
              </Fact>
              <Fact label={t('shopFile.overview.failedDocuments')}>
                <Badge tone={live.failedDocumentCount > 0 ? 'red' : 'green'}>
                  {live.failedDocumentCount}
                </Badge>
              </Fact>
              <Fact label={t('shopFile.overview.lastWebhook')}>
                {when(live.lastWebhookReceivedAt, locale)}
              </Fact>
            </dl>
          )}
        </Section>
      </div>

      <Section
        title={t('shopFile.overview.attention')}
        unavailable={unavailable['openAttentionItems']}
      >
        <p className="text-sm text-gray-600">
          {t('shopFile.overview.attentionHint')}
        </p>
        {file.openAttentionItems && file.openAttentionItems.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('shopFile.overview.columnType')}</th>
                  <th>{t('shopFile.overview.columnOrder')}</th>
                  <th>{t('shopFile.overview.columnStatus')}</th>
                  <th>{t('shopFile.overview.columnSince')}</th>
                </tr>
              </thead>
              <tbody>
                {file.openAttentionItems.map((item) => (
                  <tr key={item.id}>
                    <td className="font-mono text-xs">{item.type}</td>
                    <td className="font-mono text-xs">
                      {item.orderId ?? item.refundId ?? '—'}
                    </td>
                    <td>{item.status}</td>
                    <td>{when(item.createdAt, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-600">
            {t('shopFile.overview.noneOpen')}
          </p>
        )}
      </Section>

      <Section title={t('shopFile.overview.alerts')}>
        {file.openAlerts && file.openAlerts.length > 0 ? (
          <ul className="space-y-1 text-sm">
            {file.openAlerts.map((alert) => (
              <li
                key={alert.id}
                className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5"
              >
                <Badge tone={alert.type === 'ERROR' ? 'red' : 'amber'}>
                  {alert.type}
                </Badge>
                <span className="min-w-0 break-words">{alert.title}</span>
                <span className="text-gray-500">
                  {when(alert.createdAt, locale)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-600">
            {t('shopFile.overview.noneOpen')}
          </p>
        )}
      </Section>

      <Section
        title={t('shopFile.overview.webhooks')}
        unavailable={unavailable['recentWebhooks']}
      >
        {file.recentWebhooks && file.recentWebhooks.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('shopFile.overview.columnReceived')}</th>
                  <th>{t('shopFile.overview.columnTopic')}</th>
                  <th>{t('shopFile.overview.columnStatus')}</th>
                  <th>{t('shopFile.overview.columnOrder')}</th>
                  <th>{t('shopFile.overview.columnError')}</th>
                </tr>
              </thead>
              <tbody>
                {file.recentWebhooks.map((event) => (
                  <tr key={event.id}>
                    <td className="whitespace-nowrap">
                      {when(event.receivedAt, locale)}
                    </td>
                    <td className="font-mono text-xs">{event.topic}</td>
                    <td>
                      <Badge
                        tone={
                          event.status === 'FAILED'
                            ? 'red'
                            : event.status === 'COMPLETED'
                              ? 'green'
                              : 'gray'
                        }
                      >
                        {event.status}
                      </Badge>
                    </td>
                    <td className="font-mono text-xs">
                      {event.orderId ?? event.refundId ?? '—'}
                    </td>
                    <td className="text-xs text-red-700 max-w-md truncate">
                      {event.errorMessage ?? ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-600">
            {t('shopFile.overview.noWebhooks')}
          </p>
        )}
      </Section>

      <Section
        title={t('shopFile.overview.exports')}
        unavailable={unavailable['recentExports']}
      >
        {file.recentExports && file.recentExports.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('shopFile.overview.columnCreated')}</th>
                  <th>{t('shopFile.overview.columnFormat')}</th>
                  <th>{t('shopFile.overview.columnPeriod')}</th>
                  <th>{t('shopFile.overview.columnStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {file.recentExports.map((job) => (
                  <tr key={job.id}>
                    <td className="whitespace-nowrap">
                      {when(job.createdAt, locale)}
                    </td>
                    <td className="font-mono text-xs">
                      {job.format} · {job.exportType}
                    </td>
                    <td className="whitespace-nowrap">
                      {new Date(job.fromDate).toLocaleDateString(
                        intlLocale(locale),
                      )}{' '}
                      –{' '}
                      {new Date(job.toDate).toLocaleDateString(
                        intlLocale(locale),
                      )}
                    </td>
                    <td>
                      <Badge tone={job.status === 'failed' ? 'red' : 'gray'}>
                        {job.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-600">
            {t('shopFile.overview.noExports')}
          </p>
        )}
      </Section>

      <Section
        title={t('shopFile.overview.tickets')}
        unavailable={unavailable['tickets']}
      >
        {file.tickets && file.tickets.length > 0 ? (
          <ul className="space-y-1 text-sm">
            {file.tickets.map((ticket) => (
              <li key={ticket.id} className="flex gap-2 items-center">
                <Link
                  to={`/support/${ticket.id}`}
                  className="font-mono text-primary-700"
                >
                  {ticket.reference}
                </Link>
                <span className="truncate">{ticket.subject}</span>
                <Badge tone="gray">{ticket.status}</Badge>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-600">
            {t('shopFile.overview.noTickets')}
          </p>
        )}
      </Section>
    </PageBody>
  );
}
